/**
 * Feature Illustration — single SVG replacing the old other-page PNGs.
 *
 * Layout matches the original: 3 cards around a center logo.
 * SVG content derived from sl-website Integrative Story visuals.
 * - Top-left: Canvas (Sticky Notes from S1/S2)
 * - Top-right: Database (Table from S2)
 * - Bottom-center: Automation (Pipeline from S3)
 * - Center: StreamLain Logo
 */
import type { FC } from 'react';

const B = '#5090D3'; // brand

export const FeatureIllustration: FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    className={className}
    viewBox="0 0 670 530"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block' }}
  >
    <defs>
      <filter id="cs" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow
          dx="0"
          dy="2"
          stdDeviation="4"
          floodColor="#000"
          floodOpacity="0.2"
        />
      </filter>
      <filter id="pg" x="-10%" y="-20%" width="120%" height="140%">
        <feDropShadow
          dx="0"
          dy="1"
          stdDeviation="3"
          floodColor="#000"
          floodOpacity="0.15"
        />
      </filter>
      <linearGradient id="thg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={B} stopOpacity="0.18" />
        <stop offset="100%" stopColor={B} stopOpacity="0.08" />
      </linearGradient>
      <linearGradient id="pf1" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={B} stopOpacity="0.3" />
        <stop offset="100%" stopColor="#D8B4FE" stopOpacity="0.5" />
      </linearGradient>
      <linearGradient id="pf2" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#D8B4FE" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#6EE7B7" stopOpacity="0.5" />
      </linearGradient>
      <linearGradient id="gg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={B} stopOpacity="0.5" />
        <stop offset="100%" stopColor="#D8B4FE" stopOpacity="0.3" />
      </linearGradient>
    </defs>

    {/* ═══ TOP-LEFT: Canvas — Sticky Notes (Story S1/S2) ═══ */}
    <g transform="translate(0, 30)">
      {[
        {
          x: 10,
          y: 15,
          w: 72,
          h: 58,
          c: '#FCD34D',
          d: '#E5B82A',
          r: -6,
          lines: [42, 32, 26],
        },
        {
          x: 48,
          y: 85,
          w: 70,
          h: 56,
          c: '#6EE7B7',
          d: '#4FD49E',
          r: 4,
          lines: [38, 28, 20],
        },
        {
          x: 92,
          y: 40,
          w: 68,
          h: 55,
          c: '#C4B5FD',
          d: '#A99AE8',
          r: -2,
          lines: [44, 30],
        },
      ].map((n, i) => {
        const cx = n.x + n.w / 2;
        const cy = n.y + n.h / 2;
        return (
          <g key={i} transform={`rotate(${n.r} ${cx} ${cy})`} filter="url(#cs)">
            <rect
              x={n.x}
              y={n.y}
              width={n.w}
              height={n.h}
              rx="3"
              fill={n.c}
              opacity="0.65"
            />
            <rect
              x={n.x}
              y={n.y}
              width={n.w}
              height={n.h * 0.4}
              rx="3"
              fill="#FFF"
              opacity="0.12"
            />
            <polygon
              points={`${n.x + n.w - 10},${n.y + n.h} ${n.x + n.w},${n.y + n.h - 10} ${n.x + n.w},${n.y + n.h}`}
              fill={n.d}
              opacity="0.5"
            />
            <rect
              x={cx - 12}
              y={n.y - 4}
              width="24"
              height="8"
              rx="1"
              fill="#FFF"
              opacity="0.2"
            />
            {n.lines.map((lw, li) => (
              <rect
                key={li}
                x={n.x + 8}
                y={n.y + 16 + li * 12}
                width={lw}
                height="4"
                rx="2"
                fill="#141414"
                opacity={0.12 - li * 0.02}
              />
            ))}
          </g>
        );
      })}
    </g>

    {/* ═══ TOP-RIGHT: Database Table (Story S2) ═══ */}
    <g transform="translate(400, 30)">
      {(() => {
        const T = { x: 0, y: 0, w: 260, h: 170, hH: 30, rH: 35, rx: 10 };
        const cols = [
          { l: 'Task', w: 94 },
          { l: 'Status', w: 82 },
          { l: 'Assignee', w: 84 },
        ];
        const rows = [
          {
            t: 'Brainstorming',
            sL: 'Done',
            sC: '#6EE7B7',
            sW: 38,
            n: 'Anna',
            nC: B,
          },
          {
            t: 'Whiteboard',
            sL: 'In Progress',
            sC: '#FCD34D',
            sW: 62,
            n: 'Max',
            nC: '#6EE7B7',
          },
          {
            t: 'Research',
            sL: 'Open',
            sC: '#FCA5A5',
            sW: 38,
            n: 'Lea',
            nC: '#C4B5FD',
          },
          {
            t: 'Documentation',
            sL: 'Done',
            sC: '#6EE7B7',
            sW: 38,
            n: 'Anna',
            nC: B,
          },
        ];
        let cx = T.x + 14;
        return (
          <g>
            <rect
              x={T.x}
              y={T.y}
              width={T.w}
              height={T.h}
              rx={T.rx}
              fill="#0D1117"
              opacity="0.4"
            />
            <rect
              x={T.x}
              y={T.y}
              width={T.w}
              height={T.h}
              rx={T.rx}
              stroke={B}
              strokeWidth="1"
              opacity="0.3"
              fill="none"
            />
            <rect
              x={T.x}
              y={T.y}
              width={T.w}
              height={T.hH}
              rx={T.rx}
              fill="url(#thg)"
            />
            <line
              x1={T.x + 1}
              y1={T.y + T.hH}
              x2={T.x + T.w - 1}
              y2={T.y + T.hH}
              stroke={B}
              strokeWidth="1"
              opacity="0.25"
            />
            {cols.map((c, i) => {
              const x = cx;
              cx += c.w;
              return (
                <text
                  key={i}
                  x={x}
                  y={T.y + 20}
                  fontSize="9"
                  fontWeight="600"
                  fill="#F5F0E8"
                  opacity="0.5"
                  fontFamily="system-ui, sans-serif"
                >
                  {c.l}
                </text>
              );
            })}
            {rows.map((r, ri) => {
              const ry = T.y + T.hH + ri * T.rH;
              const cy = ry + T.rH / 2;
              const c1 = T.x + 14,
                c2 = T.x + cols[0].w + 10,
                c3 = T.x + cols[0].w + cols[1].w + 10;
              return (
                <g key={ri}>
                  {ri > 0 && (
                    <line
                      x1={T.x + 8}
                      y1={ry}
                      x2={T.x + T.w - 8}
                      y2={ry}
                      stroke={B}
                      strokeWidth="0.5"
                      opacity="0.1"
                    />
                  )}
                  <text
                    x={c1}
                    y={cy + 4}
                    fontSize="9"
                    fill="#F5F0E8"
                    opacity="0.7"
                    fontFamily="system-ui, sans-serif"
                  >
                    {r.t}
                  </text>
                  <rect
                    x={c2}
                    y={cy - 7}
                    width={r.sW}
                    height="15"
                    rx="7.5"
                    fill={r.sC}
                    opacity="0.2"
                  />
                  <text
                    x={c2 + 6}
                    y={cy + 3}
                    fontSize="7.5"
                    fontWeight="500"
                    fill={r.sC}
                    opacity="0.85"
                    fontFamily="system-ui, sans-serif"
                  >
                    {r.sL}
                  </text>
                  <circle cx={c3 + 6} cy={cy} r="6" fill={r.nC} opacity="0.3" />
                  <text
                    x={c3 + 6}
                    y={cy + 3}
                    textAnchor="middle"
                    fontSize="6"
                    fontWeight="700"
                    fill={r.nC}
                    opacity="0.8"
                  >
                    {r.n[0]}
                  </text>
                  <text
                    x={c3 + 16}
                    y={cy + 3}
                    fontSize="8"
                    fill="#F5F0E8"
                    opacity="0.55"
                    fontFamily="system-ui, sans-serif"
                  >
                    {r.n}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })()}
    </g>

    {/* ═══ CENTER: Brand Logo (via BrandLogoDefs symbol) ═══ */}
    <g transform="translate(292, 240)" opacity="0.45">
      <use href="#logo-dark" width="100" height="100" />
    </g>

    {/* ═══ BOTTOM-CENTER: Automation Pipeline (Story S3) ═══ */}
    <g transform="translate(124, 370)">
      {/* Input cards */}
      {[
        { y: 10, l: 'Invoice #247', dc: '#FCA5A5', tag: 'New', tw: 30 },
        { y: 50, l: 'LinkedIn Post', dc: '#93C5FD', tag: 'Draft', tw: 36 },
        { y: 90, l: 'Support Ticket', dc: '#FCD34D', tag: 'Open', tw: 34 },
      ].map((c, i) => (
        <g key={i} filter="url(#pg)">
          <rect
            x="5"
            y={c.y}
            width="120"
            height="34"
            rx="7"
            fill="#0D1117"
            opacity="0.45"
          />
          <rect
            x="5"
            y={c.y}
            width="120"
            height="34"
            rx="7"
            stroke={B}
            strokeWidth="0.75"
            opacity="0.2"
            fill="none"
          />
          <circle cx="18" cy={c.y + 13} r="3" fill={c.dc} opacity="0.6" />
          <text
            x="26"
            y={c.y + 16}
            fontSize="8"
            fill="#F5F0E8"
            opacity="0.7"
            fontFamily="system-ui, sans-serif"
          >
            {c.l}
          </text>
          <rect
            x="26"
            y={c.y + 21}
            width={c.tw}
            height="9"
            rx="4.5"
            fill={c.dc}
            opacity="0.15"
          />
          <text
            x="29"
            y={c.y + 28}
            fontSize="5.5"
            fontWeight="600"
            fill={c.dc}
            opacity="0.7"
            fontFamily="system-ui, sans-serif"
          >
            {c.tag}
          </text>
        </g>
      ))}
      {/* Arrows → gear */}
      <path
        d="M130 27 C145 27,152 45,165 55"
        stroke="url(#pf1)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity="0.45"
        fill="none"
      />
      <path
        d="M130 67 L165 67"
        stroke="url(#pf1)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity="0.45"
      />
      <path
        d="M130 107 C145 107,152 89,165 79"
        stroke="url(#pf1)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity="0.45"
        fill="none"
      />
      {/* Gear */}
      <g transform="translate(210 67)">
        <circle
          cx="0"
          cy="0"
          r="42"
          fill="none"
          stroke={B}
          strokeWidth="0.5"
          opacity="0.15"
        />
        <circle
          cx="0"
          cy="0"
          r="28"
          fill="url(#gg)"
          opacity="0.3"
          stroke="url(#gg)"
          strokeWidth="1.2"
        />
        {Array.from({ length: 10 }).map((_, i) => {
          const a = ((i * 36 - 90) * Math.PI) / 180;
          const co = Math.cos(a),
            si = Math.sin(a);
          const iR = 26,
            oR = 36,
            hw = 5,
            px = -si,
            py = co;
          return (
            <polygon
              key={i}
              points={`${(co * iR - px * hw).toFixed(1)},${(si * iR - py * hw).toFixed(1)} ${(co * iR + px * hw).toFixed(1)},${(si * iR + py * hw).toFixed(1)} ${(co * oR + px * hw).toFixed(1)},${(si * oR + py * hw).toFixed(1)} ${(co * oR - px * hw).toFixed(1)},${(si * oR - py * hw).toFixed(1)}`}
              fill="url(#gg)"
              opacity="0.4"
              stroke="url(#gg)"
              strokeWidth="0.8"
            />
          );
        })}
        <circle
          cx="0"
          cy="0"
          r="14"
          fill="#0D1117"
          opacity="0.55"
          stroke={B}
          strokeWidth="1"
        />
        <path
          d="M0,-6 L1.5,-2 L6,0 L1.5,2 L0,6 L-1.5,2 L-6,0 L-1.5,-2 Z"
          fill="#D8B4FE"
          opacity="0.6"
        />
      </g>
      {/* Arrows → output */}
      <path
        d="M255 57 C270 47,282 35,298 27"
        stroke="url(#pf2)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity="0.45"
        fill="none"
      />
      <path
        d="M255 67 L298 67"
        stroke="url(#pf2)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity="0.45"
      />
      <path
        d="M255 77 C270 87,282 99,298 107"
        stroke="url(#pf2)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity="0.45"
        fill="none"
      />
      {/* Output cards */}
      {[
        {
          y: 10,
          l: 'Invoice #247',
          sub: '\u20AC4.280 \u00B7 15.03.',
          sc: '#6EE7B7',
        },
        {
          y: 50,
          l: 'LinkedIn Post',
          sub: 'Scheduled \u00B7 14:00',
          sc: '#93C5FD',
        },
        {
          y: 90,
          l: 'Support Ticket',
          sub: 'Assigned \u00B7 Lea',
          sc: '#FCD34D',
        },
      ].map((c, i) => (
        <g key={i} filter="url(#pg)">
          <rect
            x="298"
            y={c.y}
            width="140"
            height="34"
            rx="7"
            fill="#0D1117"
            opacity="0.4"
          />
          <rect
            x="298"
            y={c.y}
            width="140"
            height="34"
            rx="7"
            stroke={c.sc}
            strokeWidth="0.75"
            opacity="0.2"
            fill="none"
          />
          <text
            x="312"
            y={c.y + 15}
            fontSize="8"
            fontWeight="600"
            fill="#F5F0E8"
            opacity="0.7"
            fontFamily="system-ui, sans-serif"
          >
            {c.l}
          </text>
          <text
            x="312"
            y={c.y + 26}
            fontSize="6.5"
            fill="#F5F0E8"
            opacity="0.4"
            fontFamily="system-ui, sans-serif"
          >
            {c.sub}
          </text>
          <circle
            cx="426"
            cy={c.y + 17}
            r="5"
            fill={c.sc}
            opacity="0.12"
            stroke={c.sc}
            strokeWidth="0.75"
          />
          <path
            d={`M423,${c.y + 17}l2,2 4-4`}
            stroke={c.sc}
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity="0.7"
          />
        </g>
      ))}
    </g>
  </svg>
);
